import React from 'react';
import { Card, CardContent, Typography, Avatar, Box, Paper } from '@mui/material';
import { Player } from '../types';

interface PlayerStatsProps {
  player: Player | null;
}

const PlayerStats: React.FC<PlayerStatsProps> = ({ player }) => {
  if (!player) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            Select a player to view detailed statistics
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.100' }}>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
              {player.name.split(' ').map(n => n[0]).join('')}
            </Typography>
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {player.name}
            </Typography>
            <Typography color="text.secondary">
              {player.position} • {player.team}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 2, mb: 3 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
              {player.fantasyPointsPerGame?.toFixed(1) || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fantasy Points
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
              {player.pointsPerGame?.toFixed(1) || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Points
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 'bold' }}>
              {player.reboundsPerGame?.toFixed(1) || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rebounds
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
              {player.assistsPerGame?.toFixed(1) || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assists
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">Steals</Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {player.stealsPerGame?.toFixed(1) || 'N/A'}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">Blocks</Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {player.blocksPerGame?.toFixed(1) || 'N/A'}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">Turnovers</Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {player.turnoversPerGame?.toFixed(1) || 'N/A'}
            </Typography>
          </Paper>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PlayerStats;
